import { Separator } from '@/components/ui/separator'
import { SidebarTrigger } from '@/components/ui/sidebar'
import { useAuth } from '@/features/authentication/components/auth-provider.tsx'
import { getUserInitials } from '@/lib/user-initials.ts'
import { getUserRoleDisplay } from '@/lib/user-role-display.tsx'
import { useGetUserNotifications } from '@/utils/api/membership-service'
import type { InternalNotification, InternalNotificationFeedResponse } from '@/utils/api/membership-service/models'
import type { UserDetailsResponse } from '@/utils/api/user-service/models'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useNavigate } from '@tanstack/react-router'
import { useMemo } from 'react'
import { IconBellRinging, IconChevronDown, IconLogout, IconUserCircle } from '@tabler/icons-react'
import { Breadcrumbs } from './breadcrumbs'

export function SiteHeader() {
  const { logout, user } = useAuth()
  const navigate = useNavigate()

  async function handleLogout() {
    try {
      // Mark the navigation as an intentional logout to influence guards
      window.sessionStorage.setItem('justLoggedOut', '1')
    } catch {}
    logout()
    await navigate({ to: '/login', search: {}, replace: true } as any)
  }

  async function handleAccount() {
    await navigate({ to: '/membership/profile', search: {} } as any)
  }

  async function handleNotifications() {
    await navigate({ to: '/notifications', search: {} } as any)
  }

  const userId = user?.id
  const notificationsQuery = useGetUserNotifications(userId ?? 0, {
    query: {
      enabled: typeof userId === 'number',
      staleTime: 30_000,
      refetchInterval: 60_000,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: true,
    },
  })

  const unreadCount = useMemo(() => {
    const payload = notificationsQuery.data?.data as
      | InternalNotificationFeedResponse
      | InternalNotification[]
      | undefined

    if (Array.isArray(payload)) {
      return payload.filter((item) => item.isRead !== true).length
    }

    if (payload && typeof payload.unreadCount === 'number') {
      return payload.unreadCount
    }

    if (payload && Array.isArray(payload.notifications)) {
      return payload.notifications.filter((item) => item.isRead !== true).length
    }

    return 0
  }, [notificationsQuery.data])

  const unreadLabel = unreadCount > 99 ? '99+' : unreadCount

  return (
    <header
      className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height) backdrop-blur-sm"
    >
      <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
        <SidebarTrigger className="-ml-1" />
        <Separator
          orientation="vertical"
          className="mx-2 data-[orientation=vertical]:h-4"
        />
        <Breadcrumbs />
        <div className="ml-auto flex items-center gap-3">
          {/* <Button
            variant="ghost"
            size="icon"
            className="relative h-10 w-10 bg-background/70 text-muted-foreground transition hover:-translate-y-[1px] hover:border-border hover:bg-primary/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            onClick={handleNotifications}
            aria-label="View notifications"
            title="View notifications"
            disabled={!user}
          >
            <IconBellRinging className="size-5" />
            {unreadCount > 0 && (
              <span className="bg-destructive text-destructive-foreground absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold leading-none">
                {unreadLabel}
              </span>
            )}
          </Button> */}
          <UserMenu
            user={user}
            unreadCount={unreadCount}
            unreadLabel={unreadLabel}
            onLogout={handleLogout}
            onOpenNotifications={handleNotifications}
            onOpenProfile={handleAccount}
          />
        </div>
      </div>
    </header>
  )
}

type UserMenuProps = {
  user: UserDetailsResponse | null
  unreadCount: number
  unreadLabel: number | string
  onLogout: () => Promise<void>
  onOpenProfile: () => Promise<void>
  onOpenNotifications: () => Promise<void>
}

function UserMenu({
  user,
  unreadCount,
  unreadLabel,
  onLogout,
  onOpenProfile,
  onOpenNotifications,
}: UserMenuProps) {
  if (!user) {
    return null
  }

  const primaryName = user.name?.trim() || `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim()
  const displayName = primaryName || user.username || 'Account'
  const roleDisplay = getUserRoleDisplay(user, 'Member')
  const initials = getUserInitials(user)
  const unreadBadge = unreadCount > 0 ? unreadLabel : null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex items-center gap-3 bg-background/70 px-2.5 py-1.5 transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <span className="relative inline-flex">
            <Avatar className="h-9 w-9 ring-2 ring-primary/10 shadow">
              <AvatarImage src={user.profilePicUrl ?? ''} alt={displayName} />
              <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
                {initials}
              </AvatarFallback>
            </Avatar>
            {unreadBadge ? (
              <span className="bg-primary text-primary-foreground absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full px-1 text-[11px] font-semibold leading-none shadow-sm">
                {unreadBadge}
              </span>
            ) : null}
          </span>
          <span className="hidden sm:flex flex-col items-start leading-tight">
            <span className="text-sm font-semibold text-foreground">{displayName}</span>
            <span className="text-xs text-muted-foreground">{roleDisplay}</span>
          </span>
          <IconChevronDown className="size-4 text-muted-foreground transition group-hover:text-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={10}
        className="min-w-64 rounded-xl border bg-card/95 p-2 shadow-xl backdrop-blur"
      >
        <DropdownMenuLabel className="flex items-center gap-3 px-2 py-1.5">
          <Avatar className="h-10 w-10 ring-2 ring-primary/10 shadow">
            <AvatarImage src={user.profilePicUrl ?? ''} alt={displayName} />
            <AvatarFallback className="bg-primary/10 text-sm font-semibold text-primary">
              {initials}
            </AvatarFallback>
          </Avatar>
          <div className="space-y-0.5">
            <p className="text-sm font-semibold leading-tight">{displayName}</p>
            {user.email ? <p className="text-xs text-muted-foreground">{user.email}</p> : null}
            <p className="text-[11px] uppercase tracking-wide text-muted-foreground/80">{roleDisplay}</p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => void onOpenProfile()} className="gap-2 py-2">
          <IconUserCircle className="size-4" />
          My profile
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => void onOpenNotifications()} className="gap-2 py-2">
          <IconBellRinging className="size-4" />
          Notifications
          {unreadBadge ? (
            <span className="ml-auto rounded-full bg-primary/10 px-2 text-[11px] font-semibold text-primary">
              {unreadBadge}
            </span>
          ) : null}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => void onLogout()}
          className="gap-2 py-2 text-destructive focus:text-destructive"
        >
          <IconLogout className="size-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
